/* eslint-disable max-lines-per-function */
import useEmblaCarousel from 'embla-carousel-react'
import React, { useCallback, useEffect, useState, memo, useMemo } from 'react'
// TODO: storybook is not able to resolve absolute imports. Have to fix it.
// for now using relative path
import classNames from 'classnames'
import dynamic from 'next/dynamic'
import { TCarouselDotsProps, CarouselProps } from './types'
import { useRecursiveTimeout } from '@/hooks/useRecursiveTimeout'
import noop from '@/root/utils/noop'

const NextButton = dynamic<{ enabled; onClick }>(
  () => import('./CarouselButtons').then(mod => mod.NextButton),
  {
    ssr: false,
  },
)
const PrevButton = dynamic<{ enabled; onClick }>(
  () => import('./CarouselButtons').then(mod => mod.PrevButton),
  {
    ssr: false,
  },
)
const CarouselDotsComponent = dynamic<TCarouselDotsProps>(
  () => import('./CarouselDots').then(mod => mod.CarouselDotsComponent),
  {
    ssr: false,
  },
)

const AUTOPLAY_INTERVAL = 4500

const Carousel = ({
  children,
  dots = false,
  showArrows = false,
  stopAutoPlay = false,
  className,
  selectedDotColor = 'bg-gray-500',
  unselectedDotColor = 'bg-gray-300',
  selectedDotClassStyles,
  unselectedDotClassStyles,
  onDotClick = noop,
  showDotsOnTop = false,
  autoPlayInterval,
  autoPlayCallbackIrrespectiveOfIfCanScroll,
  emblaOptions,
  showDotsOnMobileOnly,
  showArrowsOnDesktop,
  onIndexChange = noop,
  navIndex,
  selectedInd,
}: CarouselProps & { onIndexChange?: (arg: number) => void }) => {
  const {
    selectedIndex,
    scrollSnaps,
    prevBtnEnabled,
    nextBtnEnabled,
    scrollPrev,
    scrollNext,
    emblaRef,
    scrollTo,
  } = useCustomEmblaCarousel({
    stopAutoPlay,
    autoPlayInterval,
    autoPlayCallbackIrrespectiveOfIfCanScroll,
    emblaOptions,
    navIndex,
    selectedInd,
  })

  const dotsComponentProps: TCarouselDotsProps = {
    scrollSnaps,
    scrollTo,
    selectedIndex,
    onDotClick,
    selectedDotClassStyles,
    selectedDotColor,
    unselectedDotClassStyles,
    unselectedDotColor,
  }

  useEffect(() => {
    onIndexChange(selectedIndex)
  }, [onIndexChange, selectedIndex])

  const dotsJSX = (
    <div className="my-3">
      <CarouselDotsComponent {...dotsComponentProps} />
    </div>
  )

  return children ? (
    <div>
      {dots && showDotsOnTop && dotsJSX}
      <div
        className={`${className ?? ''} relative overflow-hidden`}
        ref={emblaRef}
      >
        {children}
        {prevBtnEnabled && showArrows && (
          <div
            className={classNames({
              'hidden lg:block': showArrowsOnDesktop,
            })}
          >
            <PrevButton onClick={scrollPrev} enabled={prevBtnEnabled} />
          </div>
        )}
        {nextBtnEnabled && showArrows && (
          <div
            className={classNames({
              'hidden lg:block': showArrowsOnDesktop,
            })}
          >
            <NextButton onClick={scrollNext} enabled={nextBtnEnabled} />
          </div>
        )}
      </div>
      {dots && !showDotsOnTop && (
        <div
          className={classNames({
            'lg:hidden': showDotsOnMobileOnly,
          })}
        >
          {dotsJSX}
        </div>
      )}
    </div>
  ) : null
}
export default memo(Carousel)

export function useCustomEmblaCarousel(
  options: Omit<CarouselProps, 'children'>,
) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    containScroll: 'trimSnaps',
    align: 'start',
    speed: 6,
    ...options.emblaOptions,
  })
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([])
  const [prevBtnEnabled, setPrevBtnEnabled] = useState(false)
  const [nextBtnEnabled, setNextBtnEnabled] = useState(false)

  const scrollTo = useCallback(
    (index: number) => emblaApi?.scrollTo(index),
    [emblaApi],
  )

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi])
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi])

  const onSelect = useCallback(() => {
    if (!emblaApi) return
    setSelectedIndex(emblaApi.selectedScrollSnap())
    setPrevBtnEnabled(emblaApi.canScrollPrev())
    setNextBtnEnabled(emblaApi.canScrollNext())
  }, [emblaApi, setSelectedIndex])

  const autoplay = useCallback(() => {
    if (!emblaApi) return
    if (options.autoPlayCallbackIrrespectiveOfIfCanScroll) {
      options.autoPlayCallbackIrrespectiveOfIfCanScroll()
    }

    if (emblaApi.canScrollNext()) {
      emblaApi.scrollNext()
    } else {
      scrollTo(0)
    }
  }, [emblaApi, options, scrollTo])

  const forceSwipe = () => {
    const LS = window.localStorage.getItem('swiped')
    const swipeIndex = options.navIndex?.toString()
    if ((swipeIndex && !LS) || (swipeIndex && LS && swipeIndex !== LS)) {
      scrollTo(parseInt(swipeIndex, 10))
      // onDotClick(parseInt(swipeIndex, 10))
      if (typeof options.navIndex !== 'undefined')
        window.localStorage.setItem('swiped', options.navIndex.toString())
    }
  }

  useEffect(() => {
    if (emblaApi) {
      setTimeout(() => {
        forceSwipe()
      }, 300)
    }
  }, [emblaApi])

  useEffect(() => {
    if (options.selectedInd) options.selectedInd(selectedIndex)
  }, [selectedIndex, options.selectedInd])

  useEffect(() => {
    forceSwipe()
  }, [options.navIndex])

  const { play, stop } = useRecursiveTimeout(
    autoplay,
    options.autoPlayInterval ?? AUTOPLAY_INTERVAL,
  )

  const initialiseEmbla = useCallback(() => {
    if (emblaApi) {
      /**
       * if children change, i.e. for example
       * number of slides change, reinitialise
       * carousel
       * ref: https://www.embla-carousel.com/api/methods/#reinit
       * "It's also useful when adding or removing slides, or picking up any other changes like slide dimensions."
       */
      emblaApi.reInit()
      onSelect()
      setScrollSnaps(emblaApi.scrollSnapList())

      if (!options.stopAutoPlay) play()
      emblaApi.on('select', onSelect)
      emblaApi.on('pointerDown', stop)
    }
  }, [onSelect, emblaApi, play, stop, options.stopAutoPlay])

  useEffect(() => {
    // reinitialize embla when it needs to ( when the dependencies of initialiseEmbla changes)
    initialiseEmbla()
  }, [initialiseEmbla])

  const retVal = useMemo(
    () => ({
      selectedIndex,
      scrollSnaps,
      prevBtnEnabled,
      nextBtnEnabled,
      scrollPrev,
      scrollNext,
      emblaRef,
      scrollTo,
      emblaApi,
      initialiseEmbla,
    }),
    [
      nextBtnEnabled,
      prevBtnEnabled,
      scrollNext,
      scrollPrev,
      scrollSnaps,
      selectedIndex,
      emblaRef,
      scrollTo,
      emblaApi,
      initialiseEmbla,
    ],
  )

  return retVal
}
